import { Link, useLocation } from "react-router";
import { twJoin } from "tailwind-merge";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { Box } from "@/design-system/Box";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { useConfig } from "@/providers/config/useConfig";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface AnalyticsExplorePageProps {
  scope?: "project" | "portfolio";
}

type CatalogueItem = {
  title: string;
  description: string;
  to: string;
};

type CatalogueGroup = {
  title: string;
  items: CatalogueItem[];
};

/** A categorized directory of the existing analytics surfaces. */
export function AnalyticsExplorePage({ scope = "project" }: AnalyticsExplorePageProps): React.ReactNode {
  const { t } = useTranslation();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const isCommunityFeedSentimentAvailable = useConfig("isCommunityFeedSentimentAvailable");
  const isPortfolio = scope === "portfolio";
  const projectRoute = (route: (params: { slug: string }) => string) => route({ slug: slug! });
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);

  const domainGroups: CatalogueGroup[] = [
    {
      title: t("page.analytics-explore.group.participation"),
      items: [
        ...(!isPortfolio
          ? [
              {
                title: t("page.analytics-overview.overview.title"),
                description: t("page.analytics-story.introduction"),
                to: projectRoute(routes.analytics.overview),
              },
            ]
          : []),
        {
          title: t("page.analytics-events.breadcrumb"),
          description: t("page.analytics-explore.events.description"),
          to: isPortfolio
            ? portfolioRoute(routes.portfolio.analyticsExploreEvents())
            : projectRoute(routes.analytics.exploreEvents),
        },
        {
          title: t("navigation.portfolio.analytics-bookings"),
          description: t("page.analytics-explore.bookings.description"),
          to: isPortfolio
            ? portfolioRoute(routes.portfolio.analyticsExploreBookings())
            : projectRoute(routes.analytics.exploreBookings),
        },
        ...(!isPortfolio
          ? [
              {
                title: t("page.analytics-explore.adoption.title"),
                description: t("page.analytics-explore.adoption.description"),
                to: projectRoute(routes.analytics.exploreAdoption),
              },
              {
                title: t("page.analytics-explore.engagement.title"),
                description: t("page.analytics-explore.engagement.description"),
                to: projectRoute(routes.analytics.exploreEngagement),
              },
              {
                title: t("page.analytics-explore.contribution.title"),
                description: t("page.analytics-explore.contribution.description"),
                to: projectRoute(routes.analytics.exploreContribution),
              },
            ]
          : []),
      ],
    },
    {
      title: t("page.analytics-explore.group.community"),
      items: [
        {
          title: t("page.analytics-community-feed.breadcrumb"),
          description: t("page.analytics-explore.community.description"),
          to: isPortfolio
            ? portfolioRoute(routes.portfolio.analyticsExploreCommunityFeed())
            : projectRoute(routes.analytics.exploreCommunityFeed),
        },
        ...(isCommunityFeedSentimentAvailable
          ? [
              {
                title: t("page.analytics-community-feed.sentiment.breadcrumb"),
                description: t("page.analytics-explore.sentiment.description"),
                to: isPortfolio
                  ? portfolioRoute(routes.portfolio.analyticsExploreSentiment())
                  : projectRoute(routes.analytics.exploreSentiment),
              },
            ]
          : []),
      ],
    },
    {
      title: t("page.analytics-explore.group.service"),
      items: [
        {
          title: t("page.analytics-tickets.breadcrumb"),
          description: t("page.analytics-explore.tickets.description"),
          to: isPortfolio
            ? portfolioRoute(routes.portfolio.analyticsExploreTickets())
            : projectRoute(routes.analytics.exploreTickets),
        },
      ],
    },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.analytics-explore.title")}
      title={t("page.analytics-explore.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            isPortfolio
              ? { name: t("page.portfolio.title"), to: portfolioRoute(routes.portfolio.overview()) }
              : { name: t("page.analytics-story.title"), to: routes.analytics.overview({ slug: slug! }) },
            { name: t("page.analytics-explore.title") },
          ]}
        />
      }
    >
      <AnalyticsPageContent>
        <header className="max-w-measure-wide">
          <Stack gap="sm">
            <h2 className="text-heading-2 text-text-brand-secondary">{t("page.analytics-explore.heading")}</h2>
            <p className="text-body text-text-secondary">{t("page.analytics-explore.description")}</p>
          </Stack>
        </header>

        <Stack gap="xl">
          {domainGroups.map((group) => (
            <Stack key={group.title} as="section" gap="sm">
              <h2 className="text-heading-2 text-text-brand-secondary">{group.title}</h2>
              <Tiles columns={{ base: 1, container: { md: 2, lg: 3 } }} gap="sm">
                {group.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={twJoin(
                      "group min-h-card-media no-underline transition-colors hocus:bg-background-default-hover",
                      cardSurfaceVariants({ interactive: true }),
                    )}
                  >
                    <Box padding="lg">
                      <Stack gap="lg">
                        <Stack gap="sm">
                          <h3 className="text-heading-3">{item.title}</h3>
                          <p className="text-body text-text-secondary">{item.description}</p>
                        </Stack>
                        <Inline gap="xs" align="center">
                          <span className="text-body-emphasis text-text-brand-secondary">
                            {t("page.analytics-explore.view-analysis")}
                          </span>
                          <span className="transition-transform group-hocus:translate-x-0.5">
                            <ChevronRightIcon size={16} />
                          </span>
                        </Inline>
                      </Stack>
                    </Box>
                  </Link>
                ))}
              </Tiles>
            </Stack>
          ))}
        </Stack>
      </AnalyticsPageContent>
    </DocumentPage>
  );
}
